<template>
  <div class="mt-5">
    <b-steps v-model="step" :has-navigation="false" type="is-primary">
      <b-step-item step="1" label="Configuración">
        <div class="columns is-multiline">
          <div class="column is-12">
            <p class="label">Socios activos</p>
            <p class="title is-4">{{ eligibleAssociates.length }}</p>
            <p>Se crearán {{ circleCount }} círculos.</p>
          </div>
          <b-field class="column is-4" label="Máximo de socios por círculo"
            ><b-numberinput v-model="capacity" min="1" expanded controls-position="compact" @input="capacityChanged"
          /></b-field>
          <b-field class="column is-4" label="Cantidad de círculos"
            ><b-numberinput v-model="circleTarget" min="1" expanded controls-position="compact" @input="circleTargetChanged"
          /></b-field>
          <b-field class="column is-4" label="Máximo de predeterminados"
            ><b-numberinput v-model="defaultLimit" min="0" :max="maxPerCircle" expanded controls-position="compact"
          /></b-field>
          <b-field class="column is-4" label="Máximo de repetidos del mismo círculo"
            ><b-numberinput v-model="sameCircleLimit" min="1" :max="maxPerCircle" expanded controls-position="compact"
          /></b-field>
        </div>
        <b-button type="is-primary" @click="startSelection">Continuar</b-button>
      </b-step-item>

      <b-step-item step="2" label="Predeterminados">
        <p class="mb-3">Selecciona hasta {{ defaultLimit }} socios predeterminados por círculo. Comienza con <strong>Círculo 1</strong>.</p>
        <b-tabs :key="draftCircles.length" v-model="selectedCircle" type="is-boxed" expanded>
          <b-tab-item v-for="circle in draftCircles" :key="circle.number" :label="`Círculo ${circle.number}`">
            <div class="level mb-3">
              <div><strong>Socios predeterminados</strong> ({{ circle.defaults.length }}/{{ defaultLimit }})</div>
              <b-button type="is-primary" size="is-small" @click="openAddModal">Agregar socios</b-button>
            </div>
            <b-table :data="circle.defaults" narrowed striped>
              <b-table-column field="nombre" label="Socio" sortable v-slot="p"
                ><strong>{{ p.row.nombre }} {{ p.row.apellido }}</strong
                ><br /><small>{{ p.row.empresa }} · {{ p.row.email }}</small></b-table-column
              >
              <b-table-column field="circulo_nombre" label="Círculo actual" sortable v-slot="p">{{
                p.row.circulo_nombre || 'Sin círculo'
              }}</b-table-column>
              <b-table-column field="subespecialidad_nombre" label="Subespecialidad" sortable v-slot="p">{{
                p.row.subespecialidad_nombre || 'Sin subespecialidad'
              }}</b-table-column>
              <b-table-column label="Acciones" width="90" v-slot="p"
                ><b-button type="is-danger" size="is-small" icon-left="delete" title="Quitar" @click="removeDefault(p.row)"
              /></b-table-column>
            </b-table>
            <p v-if="!circle.defaults.length" class="has-text-grey py-4">Aún no has agregado socios a este círculo.</p>
          </b-tab-item>
        </b-tabs>
        <b-button type="is-primary" @click="generate">Generar simulación</b-button>
      </b-step-item>

      <b-step-item step="3" label="Resultado">
        <div class="level">
          <div>
            <p class="heading">Generado</p>
            <p>{{ generatedAt }}</p>
          </div>
          <div class="buttons">
            <b-button type="is-primary" @click="downloadCsv">Descargar CSV / Excel</b-button
            ><b-button outlined @click="step = 0">Nueva simulación</b-button>
          </div>
        </div>
        <div v-if="resultWarningCircles.length" class="notification is-warning is-light mb-4">
          <strong>Revisión necesaria:</strong>
          {{ resultWarningCircles.length === 1 ? 'el círculo' : 'los círculos' }}
          <b-tag v-for="circle in resultWarningCircles" :key="circle.number" type="is-warning" class="ml-1">
            {{ circle.number }}
          </b-tag>
          {{ resultWarningCircles.length === 1 ? 'requiere' : 'requieren' }} revisión por reglas relajadas o falta de socios compatibles.
        </div>
        <b-tabs :key="result.length" v-model="resultCircle" type="is-boxed" expanded>
          <b-tab-item v-for="circle in result" :key="circle.number" :label="`Círculo ${circle.number}`">
            <div class="level mb-3">
              <div>
                <strong>Círculo {{ circle.number }}</strong> ({{ circle.members.length }}/{{ maxPerCircle }})
              </div>
            </div>
            <p v-if="circle.warnings.length" class="has-text-warning-dark mb-3"><strong>Advertencias:</strong> {{ circle.warnings.join(' ') }}</p>
            <b-table :data="circle.members" narrowed striped>
              <b-table-column field="nombre" label="Socio" sortable v-slot="p"
                ><strong>{{ p.row.nombre }} {{ p.row.apellido }}</strong
                ><br /><small>{{ p.row.empresa }} · {{ p.row.email }}</small></b-table-column
              >
              <b-table-column field="circulo_nombre" label="Círculo actual" sortable v-slot="p">{{
                p.row.circulo_nombre || 'Sin círculo'
              }}</b-table-column>
              <b-table-column field="subespecialidad_nombre" label="Subespecialidad" sortable v-slot="p">{{
                p.row.subespecialidad_nombre || 'Sin subespecialidad'
              }}</b-table-column>
              <b-table-column label="Asignación" v-slot="p"
                ><b-tag :type="p.row._issue ? 'is-warning' : 'is-success'">{{
                  p.row._issue ? 'Con observación' : p.row._default ? 'Predeterminado' : 'Automático'
                }}</b-tag
                ><br /><small v-if="p.row._issue">{{ p.row._issue }}</small></b-table-column
              >
            </b-table>
          </b-tab-item>
        </b-tabs>
      </b-step-item>
    </b-steps>

    <b-modal v-model="addModal" has-modal-card trap-focus>
      <div class="modal-card modal-card-wide">
        <header class="modal-card-head">
          <p class="modal-card-title">Agregar socios al Círculo {{ selectedCircle + 1 }}</p>
          <button class="delete" @click="addModal = false" />
        </header>
        <section class="modal-card-body">
          <div class="columns">
            <b-field class="column is-5" label="Círculo actual"
              ><b-select v-model="currentCircleFilter" expanded
                ><option value="">Todos los círculos</option>
                <option v-for="circle in circles" :key="circle.id" :value="String(circle.id)">{{ circle.nombre }}</option></b-select
              ></b-field
            ><b-field class="column is-7" label="Buscar socio"
              ><b-input v-model="search" placeholder="Nombre, empresa, RFC o número de socio"
            /></b-field>
          </div>
          <p class="help mb-2">Seleccionados: {{ modalSelection.length }} / {{ defaultLimit }}</p>
          <b-table
            :data="availableAssociates"
            :checked-rows.sync="modalSelection"
            checkable
            :is-row-checkable="canSelect"
            paginated
            :per-page="10"
            narrowed
            striped
          >
            <b-table-column label="Socio" v-slot="p"
              ><strong>{{ p.row.nombre }} {{ p.row.apellido }}</strong
              ><br /><small>{{ p.row.empresa }} · {{ p.row.email }}</small></b-table-column
            >
            <b-table-column label="Círculo actual" v-slot="p">{{ p.row.circulo_nombre || 'Sin círculo' }}</b-table-column>
            <b-table-column label="Subespecialidad" v-slot="p">{{ p.row.subespecialidad_nombre || 'Sin subespecialidad' }}</b-table-column>
          </b-table>
        </section>
        <footer class="modal-card-foot"><b-button type="is-primary" @click="addSelected">Agregar seleccionados</b-button></footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  data: () => ({
    step: 0,
    capacity: 10,
    circleTarget: 10,
    defaultLimit: 3,
    sameCircleLimit: 3,
    selectedCircle: 0,
    resultCircle: 0,
    search: '',
    currentCircleFilter: '',
    draftCircles: [],
    result: [],
    generatedAt: '',
    addModal: false,
    modalSelection: [],
  }),
  computed: {
    ...mapState({ associates: (s) => s.circleSimulator.associates, circles: (s) => s.circleSimulator.circles }),
    circleCount() {
      return Math.ceil(this.eligibleAssociates.length / Math.max(1, Number(this.capacity)));
    },
    maxPerCircle() {
      return Math.max(1, Number(this.capacity));
    },
    resultWarningCircles() {
      return this.result.filter((circle) => circle.warnings && circle.warnings.length);
    },
    eligibleAssociates() {
      return this.associates.filter((associate) => Number(associate.circulo_id) > 0);
    },
    selectedCircleDefaults: {
      get() {
        return this.draftCircles[this.selectedCircle] ? this.draftCircles[this.selectedCircle].defaults : [];
      },
      set(value) {
        if (this.draftCircles[this.selectedCircle]) this.draftCircles[this.selectedCircle].defaults = value.slice(0, this.defaultLimit);
      },
    },
    availableAssociates() {
      const q = this.search.toLowerCase();
      const selected = new Set(this.selectedCircleDefaults.map((a) => a.id));
      return this.eligibleAssociates.filter(
        (a) =>
          !selected.has(a.id) &&
          (!this.currentCircleFilter || String(a.circulo_id) === this.currentCircleFilter) &&
          (!q || [a.nombre, a.apellido, a.empresa, a.email, a.rfc, a.socio].join(' ').toLowerCase().includes(q)),
      );
    },
  },
  async mounted() {
    await this.load();
    this.capacity = Math.max(1, Math.ceil(this.eligibleAssociates.length / 10));
  },
  watch: {
    step(value) {
      if (value === 1)
        this.$nextTick(() => {
          this.selectedCircle = 0;
        });
      if (value === 2)
        this.$nextTick(() => {
          this.resultCircle = 0;
        });
    },
  },
  methods: {
    ...mapActions({ load: 'circleSimulator/load' }),
    startSelection() {
      this.draftCircles = Array.from({ length: this.circleCount }, (_, i) => ({ number: i + 1, defaults: [] }));
      this.selectedCircle = 0;
      this.step = 1;
    },
    capacityChanged(value) {
      const capacity = Math.max(1, Number(value));
      this.circleTarget = Math.max(1, Math.ceil(this.eligibleAssociates.length / capacity));
    },
    circleTargetChanged(value) {
      const circles = Math.max(1, Number(value));
      this.capacity = Math.max(1, Math.ceil(this.eligibleAssociates.length / circles));
    },
    openAddModal() {
      this.search = '';
      this.currentCircleFilter = '';
      this.modalSelection = [];
      this.addModal = true;
    },
    canSelect(row) {
      return this.modalSelection.some((a) => a.id === row.id) || this.modalSelection.length < this.defaultLimit;
    },
    addSelected() {
      this.draftCircles[this.selectedCircle].defaults = this.modalSelection.slice(0, this.defaultLimit);
      this.modalSelection = [];
      this.addModal = false;
    },
    removeDefault(row) {
      this.draftCircles[this.selectedCircle].defaults = this.selectedCircleDefaults.filter((a) => a.id !== row.id);
    },
    generate() {
      const used = new Set();
      this.result = this.draftCircles.map((draft) => {
        const members = [];
        const warnings = [];
        draft.defaults.forEach((a) => {
          if (members.length < this.maxPerCircle && !used.has(a.id)) {
            members.push({ ...a, _default: true });
            used.add(a.id);
          }
        });
        const candidates = this.eligibleAssociates.filter((a) => !used.has(a.id)).sort(() => Math.random() - 0.5);
        candidates.forEach((a) => {
          if (members.length >= this.maxPerCircle) return;
          const duplicate = a.subespecialidad_id && members.some((m) => m.subespecialidad_id && m.subespecialidad_id === a.subespecialidad_id);
          const previousCount =
            Number(a.circulo_id) > 0 ? members.filter((m) => Number(m.circulo_id) > 0 && Number(m.circulo_id) === Number(a.circulo_id)).length : 0;
          if (!duplicate && previousCount < this.sameCircleLimit) {
            members.push({ ...a, _default: false });
            used.add(a.id);
          }
        });
        if (members.length < this.maxPerCircle) {
          candidates.forEach((a) => {
            if (members.length < this.maxPerCircle && !used.has(a.id)) {
              const duplicate = a.subespecialidad_id && members.some((m) => m.subespecialidad_id && m.subespecialidad_id === a.subespecialidad_id);
              const previousCount =
                Number(a.circulo_id) > 0
                  ? members.filter((m) => Number(m.circulo_id) > 0 && Number(m.circulo_id) === Number(a.circulo_id)).length
                  : 0;
              const reasons = [];
              if (duplicate) reasons.push('repite subespecialidad');
              if (previousCount >= this.sameCircleLimit) reasons.push(`supera el máximo de ${this.sameCircleLimit} socios del mismo círculo actual`);
              members.push({
                ...a,
                _issue: reasons.length ? `Se agregó porque ${reasons.join(' y ')}.` : 'Se agregó por falta de candidatos compatibles.',
              });
              used.add(a.id);
            }
          });
          if (members.length < this.maxPerCircle) {
            warnings.push(`No fue posible llenar el círculo: faltan ${this.maxPerCircle - members.length} socio(s) compatibles.`);
          } else {
            warnings.push('Fue necesario relajar alguna regla para completar el círculo.');
          }
        }
        return { number: draft.number, members, warnings };
      });
      this.generatedAt = new Date().toLocaleString('es-MX');
      this.resultCircle = 0;
      this.step = 2;
    },
    downloadCsv() {
      const rows = [['Círculo', 'Socio', 'Nombre', 'Empresa', 'RFC', 'Círculo actual', 'Categoría', 'Subespecialidad', 'Asignación']];
      this.result.forEach((c) =>
        c.members.forEach((a) =>
          rows.push([
            c.number,
            a.socio,
            `${a.nombre} ${a.apellido}`,
            a.empresa,
            a.rfc,
            a.circulo_nombre || '',
            a.categoria_nombre || '',
            a.subespecialidad_nombre || '',
            this.draftCircles[c.number - 1].defaults.some((d) => d.id === a.id) ? 'Predeterminado' : 'Automático',
          ]),
        ),
      );
      const csv = rows.map((r) => r.map((v) => `"${String(v || '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `simulacion-circulos-${Date.now()}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.modal-card-wide {
  width: 90vw;
  max-width: 1100px;
}
</style>
